interface CategoryBudget {
  category: string;
  planned: number;
}

interface MonthlyBudget {
  year: number;
  month: string; // Month name like "January"
  totalExpenseBudget: number;
  totalIncomeBudget: number;
  expenseCategoryBudgets: CategoryBudget[];
  incomeCategoryBudgets: CategoryBudget[];
}

const BUDGET_STORAGE_KEY = 'pocketBalance_budgets';

export class BudgetStorage {
  
  /**
   * Get all budgets from local storage
   */
  static getAllBudgets(): MonthlyBudget[] {
    try {
      const stored = localStorage.getItem(BUDGET_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading budgets from localStorage:', error);
      return [];
    }
  }

  /**
   * Get budget for a specific month and year
   */
  static getBudgetForMonth(year: number, month: string): MonthlyBudget | null {
    const budgets = this.getAllBudgets();
    return budgets.find(b => b.year === year && b.month === month) || null;
  }

  /**
   * Save or update budget for a specific month
   */
  static saveBudgetForMonth(monthlyBudget: MonthlyBudget): void {
    try {
      const budgets = this.getAllBudgets();
      const existingIndex = budgets.findIndex(
        b => b.year === monthlyBudget.year && b.month === monthlyBudget.month
      );

      if (existingIndex >= 0) {
        budgets[existingIndex] = monthlyBudget;
      } else {
        budgets.push(monthlyBudget);
      }
      localStorage.setItem(BUDGET_STORAGE_KEY, JSON.stringify(budgets));
    } catch (error) {
      console.error('Error saving budget to localStorage:', error);
      throw new Error('Failed to save budget');
    }
  }

  /**
   * Get planned amount for a specific category in a month
   */
  static getCategoryBudget(year: number, month: string, category: string, isExpense: boolean): number {
    const monthlyBudget = this.getBudgetForMonth(year, month);
    if (!monthlyBudget) return 0;
    
    const categoryBudgets = isExpense ? monthlyBudget.expenseCategoryBudgets : monthlyBudget.incomeCategoryBudgets;
    const categoryBudget = categoryBudgets.find(cb => cb.category === category);
    return categoryBudget?.planned || 0;
  }

  /**
   * Get total planned amount for a month (expenses or income)
   */
  static getTotalBudget(year: number, month: string, isExpense: boolean): number {
    const monthlyBudget = this.getBudgetForMonth(year, month);
    return isExpense ? (monthlyBudget?.totalExpenseBudget || 0) : (monthlyBudget?.totalIncomeBudget || 0);
  }

  /**
   * Update category budget and recalculate total
   */
  static updateCategoryBudget(
    year: number, 
    month: string, 
    category: string, 
    plannedAmount: number,
    isExpense: boolean
  ): void {
    let monthlyBudget = this.getBudgetForMonth(year, month);
    
    if (!monthlyBudget) {
      monthlyBudget = {
        year,
        month,
        totalExpenseBudget: 0,
        totalIncomeBudget: 0,
        expenseCategoryBudgets: [],
        incomeCategoryBudgets: []
      };
    }

    // Update or add category budget
    const categoryBudgets = isExpense ? monthlyBudget.expenseCategoryBudgets : monthlyBudget.incomeCategoryBudgets;
    const existingIndex = categoryBudgets.findIndex(cb => cb.category === category);
    if (existingIndex >= 0) {
      categoryBudgets[existingIndex].planned = plannedAmount;
    } else {
      categoryBudgets.push({ category, planned: plannedAmount });
    }

    // Recalculate total budget
    if (isExpense) {
      monthlyBudget.totalExpenseBudget = monthlyBudget.expenseCategoryBudgets.reduce(
        (sum, cb) => sum + cb.planned, 
        0
      );
    } else {
      monthlyBudget.totalIncomeBudget = monthlyBudget.incomeCategoryBudgets.reduce(
        (sum, cb) => sum + cb.planned, 
        0
      );
    }

    this.saveBudgetForMonth(monthlyBudget);
  }

  /**
   * Set total budget and distribute proportionally to categories if they exist
   */
  static setTotalBudget(
    year: number, 
    month: string, 
    totalBudget: number, 
    categories: string[] = [], 
    isExpense: boolean
  ): void {
    let monthlyBudget = this.getBudgetForMonth(year, month);
    
    if (!monthlyBudget) {
      monthlyBudget = {
        year,
        month,
        totalExpenseBudget: isExpense ? totalBudget : 0,
        totalIncomeBudget: isExpense ? 0 : totalBudget,
        expenseCategoryBudgets: isExpense ? categories.map(category => ({ category, planned: 0 })) : [],
        incomeCategoryBudgets: isExpense ? [] : categories.map(category => ({ category, planned: 0 }))
      };
    } else {
      if (isExpense) {
        monthlyBudget.totalExpenseBudget = totalBudget;
        
        // If no category budgets exist yet, distribute evenly
        if (monthlyBudget.expenseCategoryBudgets.length === 0 && categories.length > 0) {
          const perCategory = totalBudget / categories.length;
          monthlyBudget.expenseCategoryBudgets = categories.map(category => ({
            category,
            planned: perCategory
          }));
        }
      } else {
        monthlyBudget.totalIncomeBudget = totalBudget;
        
        // If no category budgets exist yet, distribute evenly
        if (monthlyBudget.incomeCategoryBudgets.length === 0 && categories.length > 0) {
          const perCategory = totalBudget / categories.length;
          monthlyBudget.incomeCategoryBudgets = categories.map(category => ({
            category,
            planned: perCategory
          }));
        }
      }
    }

    this.saveBudgetForMonth(monthlyBudget);
  }

  /**
   * Delete budget for a specific month
   */
  static deleteBudgetForMonth(year: number, month: string): void {
    try {
      const budgets = this.getAllBudgets();
      const filteredBudgets = budgets.filter(
        b => !(b.year === year && b.month === month)
      );
      localStorage.setItem(BUDGET_STORAGE_KEY, JSON.stringify(filteredBudgets));
    } catch (error) {
      console.error('Error deleting budget from localStorage:', error);
    }
  }

  /**
   * Clear all budget data
   */
  static clearAllBudgets(): void {
    try {
      localStorage.removeItem(BUDGET_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing budgets from localStorage:', error);
    }
  }
}

export type { CategoryBudget, MonthlyBudget }; 